import { Component, signal } from '@angular/core';
import { ProductStatus as ProductStatusData } from '@mira-avant/mtlibs';
import { AngularModule } from '../../../../modules/angular.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { ProductStatusForm } from '../../../forms/standard/product-status-form/product-status-form';
import { ProductStatusService } from '../../../../services/product-status';
import { TableFormContainer } from '../../../ui/admin/table-form-container/table-form-container';
import { Subscription } from 'rxjs/internal/Subscription';
import { RowColumn } from '../../../ui/admin/cms-table/row-column';
import { HeaderColumn } from '../../../ui/admin/cms-table/header-column';
import {
	Column,
	CmsTable,
	ENABLE_EDIT_COLUMN,
	ENABLE_DELETE_COLUMN,
	ENABLE_UP_DOWN_COLUMN,
} from '../../../ui/admin/cms-table/cms-table';
import { BreadcrumbsComponent } from '../../../ui/admin/breadcrumbs/breadcrumbs.component';

@Component({
	selector: 'app-product-status',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		NzCardModule,
		NzDividerModule,
		ProductStatusForm,
		TableFormContainer,
		CmsTable,
		RowColumn,
		HeaderColumn,
		BreadcrumbsComponent,
	],
	templateUrl: './product-status.html',
	styleUrl: './product-status.scss',
})
export class ProductStatus {
	readonly loading = signal<boolean>(false);
	readonly productStatuses = signal<ProductStatusData[]>([]);
	readonly editProductStatus = signal<ProductStatusData | undefined>(undefined);
	readonly columns = signal<Column[]>([]);
	readonly isFormShowing = signal<boolean>(false);

	private sub: Subscription | undefined;

	constructor(private productStatusService: ProductStatusService) {
		this.sub = this.productStatusService.productStatus$.subscribe(productStatuses => {
			this.productStatuses.set(productStatuses);
			this.updateTableDetails();
			this.loading.set(false);
		});
	}

	ngOnInit(): void {}

	private updateTableDetails(): void {
		const columns: Column[] = [
			{
				key: 'status',
				name: 'Name',
				sortOrder: null,
				sortFn: (a: ProductStatusData, b: ProductStatusData) => a.status.localeCompare(b.status || '') || 0,
				sortDirections: ['ascend', 'descend', null],
				filterMultiple: true,
				filterOptions: this.productStatuses().map(row => ({
					text: row.status || '',
					value: row.status || '',
				})),
				filterFn: (list: string[], item: ProductStatusData) =>
					list.some(status => item.status.indexOf(status) !== -1),
			},
			{
				key: 'productName',
				name: 'Product Name',
				sortOrder: null,
				sortFn: (a: ProductStatusData, b: ProductStatusData) =>
					a.product_name.localeCompare(b.product_name || '') || 0,
				sortDirections: ['ascend', 'descend', null],
				filterMultiple: true,
				filterOptions: Array.from(new Set(this.productStatuses().map(row => row.product_name || ''))).map(
					name => ({
						text: name,
						value: name,
					})
				),
				filterFn: (list: string[], item: ProductStatusData) =>
					list.some(name => item.product_name.indexOf(name) !== -1),
			},
			{
				key: 'displayOrder',
				name: 'Display Order',
				sortOrder: null,
				sortFn: (a: ProductStatusData, b: ProductStatusData) => (a.display_order || 0) - (b.display_order || 0),
				sortDirections: ['ascend', 'descend', null],
				filterMultiple: true,
				filterOptions: Array.from(new Set(this.productStatuses().map(row => row.display_order || 0))).map(
					order => ({
						text: order.toString(),
						value: order,
					})
				),
				filterFn: (list: number[], item: ProductStatusData) => list.some(order => item.display_order === order),
			},
		];

		columns.push(ENABLE_EDIT_COLUMN);
		columns.push(ENABLE_UP_DOWN_COLUMN);
		columns.push(ENABLE_DELETE_COLUMN);

		this.columns.set(columns);
	}

	public edit($event: object): void {
		const productStatus = $event as ProductStatusData;
		this.editProductStatus.set(this.editProductStatus()?.uuid === productStatus.uuid ? undefined : productStatus);
		this.isFormShowing.set(this.editProductStatus() !== undefined);
	}

	public onShowForm(): void {
		this.isFormShowing.set(true);
	}

	public onHideForm(): void {
		this.isFormShowing.set(false);
		this.resetEditState();
	}

	private resetEditState(): void {
		this.editProductStatus.set(undefined);
	}

	public async delete$($event: unknown): Promise<void> {
		const productStatus = $event as ProductStatusData;

		this.loading.set(true);

		try {
			await this.productStatusService.delete$(productStatus.uuid);
			this.productStatuses.set(this.productStatuses().filter(c => c.uuid !== productStatus.uuid));
		} catch (error) {
			console.error('Error deleting product status:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveUp$($event: unknown): Promise<void> {
		const productStatus = $event as ProductStatusData;

		this.loading.set(true);
		try {
			await this.productStatusService.moveUp$(productStatus.uuid);
		} catch (error) {
			console.error('Error moving product status up:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveDown$($event: unknown): Promise<void> {
		const productStatus = $event as ProductStatusData;

		this.loading.set(true);
		try {
			await this.productStatusService.moveDown$(productStatus.uuid);
		} catch (error) {
			console.error('Error moving product status down:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async onProductStatusFormSubmit$($event: any): Promise<void> {
		let submitSucceeded = false;
		this.loading.set(true);
		try {
			if (this.editProductStatus()) {
				await this.updateProductStatus$($event);
			} else {
				await this.createNewProductStatus$($event);
			}

			submitSucceeded = true;
		} catch (error) {
			console.error('Error submitting product status form:', error);
		} finally {
			this.loading.set(false);
			this.resetEditState();

			if (submitSucceeded) {
				this.isFormShowing.set(false);
			}
		}
	}

	private async createNewProductStatus$($event: any): Promise<void> {
		const productStatus = $event as ProductStatusData;
		await this.productStatusService.create$(productStatus);
	}

	private async updateProductStatus$($event: any): Promise<void> {
		const currentEditProductStatus = this.editProductStatus();
		if (!currentEditProductStatus) {
			throw new Error('No product status to update');
		}

		const productStatus = $event as ProductStatusData;
		await this.productStatusService.update$(currentEditProductStatus.uuid, productStatus);
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
